'use client';

import { AnimatePresence, motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { LoadingIcon } from './LoadingIcon';
import { colors } from '../../theme/color';
import { debounce } from 'lodash-es';

type Props = React.PropsWithChildren & {
  loading: boolean;
};

export const LoadingContainer: React.FC<Props> = ({ loading, children, ...props }) => {
  const [debouncedLoading, setDebouncedLoading] = useState(loading);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!loading) {
      debounce(() => {
        if (typeof window === 'undefined') return;

        setCount((curr) => curr + 1);
        setDebouncedLoading(false);
      }, 100)();
    } else {
      queueMicrotask(() => {
        setDebouncedLoading(true);
      });
    }
  }, [loading]);

  return (
    <MainContainer {...props}>
      {debouncedLoading ? (
        <AnimatePresence mode="wait">
          <LoadingIconContainer
            key={count}
            initial={{ scale: 0.5 }}
            animate={{
              scale: 1,
              transition: { duration: 1 },
            }}
            exit={{ scale: 0, transition: { duration: 0.5 } }}
          >
            <LoadingIcon />
          </LoadingIconContainer>
        </AnimatePresence>
      ) : (
        <AnimatePresence>
          {!loading && !debouncedLoading && (
            <Container
              key="content"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto', transition: { delay: 0.5, duration: 0.5 } }}
            >
              {children}
            </Container>
          )}
        </AnimatePresence>
      )}
    </MainContainer>
  );
};

const MainContainer = styled.div`
  width: 100%;
  display: flex;
  position: relative;
`;

const Container = styled(motion.div)`
  width: 100%;
`;

export const LoadingIconContainer = styled(motion.div)`
  width: 100%;
  padding-top: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${colors.textDefault};
  position: absolute;
  left: 0;
  right: 0;
`;
